import React, { useEffect, useState, createContext, useContext } from 'react';
type Language = 'en' | 'fr';
interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}
const translations = {
  en: {
    // Hero
    'hero.est': 'Est. 2024 • Stories of African Founders',
    'hero.title1': 'AFRICAN',
    'hero.title2': 'FOUNDERS',
    'hero.description': "The stories of African builders. Documenting the wisdom, resilience, and vision of those shaping the continent's future.",
    'hero.startListening': 'Start Listening',
    'hero.caption': "Preserving the stories of visionaries who built Africa's future",
    'hero.scroll': 'Scroll to Explore',
    // Featured Episodes
    'episodes.collection': 'Featured Collection',
    'episodes.selected': 'SELECTED',
    'episodes.chapters': 'CHAPTERS',
    'episodes.new': 'NEW EPISODE',
    'episodes.available': '1 REEL AVAILABLE',
    'episodes.listen': 'Listen on Apple Podcasts',
    'episodes.listenOn': 'Listen on',
    'episodes.ep1.title': 'The incorruptible entrepreneur: From $75 to $10 billion',
    'episodes.ep1.desc': 'The story of Strive Masiyiwa, who started with $75 to build a telecommunications empire of over $10 billion. A tale of patience, principles, and an absolute refusal to compromise.',
    'episodes.ep1.duration': 'Long-form',
    // About Hosts
    'about.eyebrow': 'ABOUT THE HOSTS',
    'about.p1': "After studying the journeys of history's greatest African entrepreneurs, we realized something unprecedented: the continent's most successful business leaders actively seek to share their wisdom. They recognize the unique power of connecting their current challenges to centuries of entrepreneurial resilience.",
    'about.p2.start': 'We capture the conversations that can only happen between those who have studied the craft of building companies for years and ',
    'about.p2.muted': 'those who are mastering it in real time.',
    'about.button': 'Learn more about the hosts',
    'about.host1.name': 'Bashir Diaby',
    'about.host1.role': 'Host & Producer',
    'about.host2.name': 'Davys Convolbo',
    'about.host2.role': 'Host & Producer',
    // Carousel
    'carousel.dangote.title': 'Founder, Dangote Group',
    'carousel.dangote.quote': "To build a business that lasts, you must be patient, disciplined, and willing to take risks others won't.",
    'carousel.fakhoury.title': 'Founder, PFO',
    'carousel.fakhoury.quote': 'A building that has no relationship with its environment is already obsolete.',
    'carousel.masiyiwa.title': 'Founder, Econet Wireless',
    'carousel.masiyiwa.quote': 'You cannot build a great institution without values. Money alone is never enough.',
    'carousel.oppenheimer.title': 'Former Chairman, De Beers',
    'carousel.oppenheimer.quote': "I don't believe in short-termism. The only way to build something meaningful is to think long term.",
    'carousel.ibrahim.title': 'Founder, Celtel',
    'carousel.ibrahim.quote': 'Leadership is about serving the people, not enriching yourself.',
    'carousel.rebrab.title': 'Founder, Cevital',
    'carousel.rebrab.quote': 'An entrepreneur must accept risk, but never compromise on integrity.',
    // Footer
    'footer.desc': 'An intellectual home for the builders of tomorrow. Preserving the wisdom, failures, and triumphs of African entrepreneurship.',
    'footer.collection': 'Collection',
    'footer.latest': 'Latest Episodes',
    'footer.about': 'About',
    'footer.index': 'Founders Index',
    'footer.series': 'Curated Series',
    'footer.connect': 'Connect',
    'footer.rights': '© 2024 African Founders Pod. All rights reserved.',
    'footer.privacy': 'Privacy',
    'footer.terms': 'Terms',
    'footer.sitemap': 'Sitemap'
  },
  fr: {
    // Hero
    'hero.est': 'Fondé en 2024 • Histoires de Fondateurs Africains',
    'hero.title1': 'AFRICAN',
    'hero.title2': 'FOUNDERS',
    'hero.description': "Les histoires des bâtisseurs africains. Documenter la sagesse, la résilience et la vision de ceux qui façonnent l'avenir du continent.",
    'hero.startListening': "Commencer l'écoute",
    'hero.caption': "Préserver les histoires des visionnaires qui ont bâti l'avenir de l'Afrique",
    'hero.scroll': 'Défiler pour explorer',
    // Featured Episodes
    'episodes.collection': 'Collection en vedette',
    'episodes.selected': 'CHAPITRES',
    'episodes.chapters': 'SÉLECTIONNÉS',
    'episodes.new': 'NOUVEL ÉPISODE',
    'episodes.available': '1 ÉPISODE DISPONIBLE',
    'episodes.listen': 'Écouter sur Apple Podcasts',
    'episodes.listenOn': 'Écouter sur',
    'episodes.ep1.title': "L'incorruptible entrepreneur : De 75 à 10 milliards USD",
    'episodes.ep1.desc': "L'histoire de Strive Masiyiwa, parti de 75 dollars pour bâtir un empire des télécommunications de plus de 10 milliards. Un récit de patience, de principes et d'un refus catégorique du compromis.",
    'episodes.ep1.duration': 'Format long',
    // About Hosts
    'about.eyebrow': 'À PROPOS DES HÔTES',
    'about.p1': "Après avoir étudié les parcours des plus grands entrepreneurs africains de l'histoire, nous avons réalisé une chose inédite : les chefs d'entreprise les plus prospères du continent cherchent activement à partager leur sagesse. Ils reconnaissent le pouvoir unique de lier leurs défis actuels à des siècles de résilience entrepreneuriale.",
    'about.p2.start': "Nous capturons les conversations qui ne peuvent avoir lieu qu'entre ceux qui étudient l'art de bâtir des entreprises depuis des années et ",
    'about.p2.muted': 'ceux qui le maîtrisent en temps réel.',
    'about.button': 'En savoir plus sur les hôtes',
    'about.host1.name': 'Bashir Diaby',
    'about.host1.role': 'Hôte et Producteur',
    'about.host2.name': 'Davys Convolbo',
    'about.host2.role': 'Hôte et Producteur',
    // Carousel
    'carousel.dangote.title': 'Fondateur, Groupe Dangote',
    'carousel.dangote.quote': "Pour bâtir une entreprise qui dure, vous devez être patient, discipliné et prêt à prendre des risques que d'autres refusent.",
    'carousel.fakhoury.title': 'Fondateur, PFO',
    'carousel.fakhoury.quote': "Un bâtiment qui n'a aucune relation avec son environnement est déjà obsolète.",
    'carousel.masiyiwa.title': 'Fondateur, Econet Wireless',
    'carousel.masiyiwa.quote': "Vous ne pouvez pas bâtir une grande institution sans valeurs. L'argent seul ne suffit jamais.",
    'carousel.oppenheimer.title': 'Ancien Président, De Beers',
    'carousel.oppenheimer.quote': 'Je ne crois pas au court-termisme. La seule façon de construire quelque chose de significatif est de penser à long terme.',
    'carousel.ibrahim.title': 'Fondateur, Celtel',
    'carousel.ibrahim.quote': "Le leadership consiste à servir les gens, pas à s'enrichir soi-même.",
    'carousel.rebrab.title': 'Fondateur, Cevital',
    'carousel.rebrab.quote': 'Un entrepreneur doit accepter le risque, mais ne jamais compromettre son intégrité.',
    // Footer
    'footer.desc': "Un foyer intellectuel pour les bâtisseurs de demain. Préserver la sagesse, les échecs et les triomphes de l'entrepreneuriat africain.",
    'footer.collection': 'Collection',
    'footer.latest': 'Derniers Épisodes',
    'footer.about': 'À propos',
    'footer.index': 'Index des Fondateurs',
    'footer.series': 'Séries Organisées',
    'footer.connect': 'Connexion',
    'footer.rights': '© 2024 African Founders Pod. Tous droits réservés.',
    'footer.privacy': 'Confidentialité',
    'footer.terms': 'Conditions',
    'footer.sitemap': 'Plan du site'
  }
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLangState] = useState<Language>('en');
  useEffect(() => {
    const savedLang = localStorage.getItem('app_lang') as Language;
    if (savedLang && (savedLang === 'en' || savedLang === 'fr')) {
      setLangState(savedLang);
    }
  }, []);
  const setLang = (newLang: Language) => {
    setLangState(newLang);
    localStorage.setItem('app_lang', newLang);
  };
  const t = (key: string): string => {
    // @ts-ignore
    return translations[lang][key] || key;
  };
  return <LanguageContext.Provider value={{
    lang,
    setLang,
    t
  }} data-id="element-208">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}